import React from "react";
import {
  FaCalendarAlt,
  FaChartLine,
  FaFistRaised,
  FaMedal,
  FaTrophy,
  FaUsers,
} from "react-icons/fa";

import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import ScrollIndicator from "../../Home/ScrollIndicator";
import useScrollToTop from "../../../hooks/useScollToTop";

import CoachStatsHero from "./components/CoachStatsHero";
import CoachStatsFilters from "./components/CoachStatsFilters";
import CoachStatCard from "./components/CoachStatCard";
import CombatResultsDonut from "./components/CombatResultsDonut";
import ClassementBarChart from "./components/ClassementBarChart";
import TopJudokasTable from "./components/TopJudokasTable";
import StatsSkeleton from "./components/StatsSkeleton";
import StatsEmptyState from "./components/StatsEmptyState";

import { useCoachStatsPage } from "./hooks/useCoachStatsPage";

function StatistiquesGroupesJudokaPage() {
  useScrollToTop();

  const {
    groups,
    adherents,
    filters,
    stats,
    judokaSummaries,
    filteredRows,
    scopeLabel,
    loading,
    refreshing,
    fetchData,
    updateFilter,
    resetFilters,
  } = useCoachStatsPage();

  const hasData =
    stats.participations > 0 ||
    stats.totalCombats > 0 ||
    stats.judokas > 0;

  return (
    <main className="min-h-screen bg-slate-50 pb-24 text-slate-950 dark:bg-slate-950 dark:text-white">
      <CoachStatsHero
        stats={stats}
        refreshing={refreshing}
        onRefresh={() => fetchData({ silent: false })}
      />

      <section className="mx-auto -mt-12 w-full max-w-[1280px] px-4 sm:px-6 lg:px-8">
        <CoachStatsFilters
          groups={groups}
          adherents={adherents}
          filters={filters}
          scopeLabel={scopeLabel}
          onChange={updateFilter}
          onReset={resetFilters}
        />
      </section>

      <section className="mx-auto mt-6 w-full max-w-[1280px] px-4 sm:px-6 lg:px-8">
        {loading ? (
          <StatsSkeleton />
        ) : !hasData ? (
          <StatsEmptyState />
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <CoachStatCard
                icon={FaUsers}
                label="Judokas analysés"
                value={stats.judokas}
                description="Nombre de judokas pris en compte dans les filtres."
                tone="blue"
              />

              <CoachStatCard
                icon={FaCalendarAlt}
                label="Participations"
                value={stats.participations}
                description="Total des inscriptions aux compétitions."
                tone="slate"
                details={[
                  { label: "Effectuées", value: stats.competitionsEffectuees },
                  { label: "À venir", value: stats.competitionsAVenir },
                ]}
              />

              <CoachStatCard
                icon={FaChartLine}
                label="Taux de victoires"
                value={`${stats.victoryRate}%`}
                description="Pourcentage de combats gagnés sur les combats saisis."
                tone="green"
              />

              <CoachStatCard
                icon={FaFistRaised}
                label="Total combats"
                value={stats.totalCombats}
                description="Victoires, défaites et matchs nuls enregistrés."
                tone="blue"
                details={[
                  { label: "✅", value: stats.wins },
                  { label: "❌", value: stats.losses },
                  { label: "N", value: stats.draws },
                ]}
              />

              <CoachStatCard
                icon={FaTrophy}
                label="Podiums"
                value={stats.podiums}
                description="Nombre de classements 1er, 2ème ou 3ème."
                tone="amber"
              />

              <CoachStatCard
                icon={FaMedal}
                label="Résultats saisis"
                value={filteredRows.length}
                description="Nombre de lignes compétition analysées dans cette vue."
                tone="slate"
              />
            </div>

            <div className="mt-6 grid gap-5 lg:grid-cols-2">
              <CombatResultsDonut stats={stats} />
              <ClassementBarChart classements={stats.classements} />
            </div>

            <div className="mt-6">
              <TopJudokasTable summaries={judokaSummaries} />
            </div>
          </>
        )}
      </section>

      <DashboardNavigation />
      <ScrollIndicator />
    </main>
  );
}

export default StatistiquesGroupesJudokaPage;